import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icons';
import { useConfirm } from './ConfirmProvider';

const ROLE_LABEL = { superadmin: 'Super Admin', amministratore: 'Amministratore', sviluppatore: 'Sviluppatore', tecnico: 'Tecnico' };

// Trigger nel piede della sidebar (avatar + nome + ruolo) che apre un menu a
// comparsa con le azioni sull'account (Impostazioni, uscita) — invece di due
// pulsanti icona sempre visibili sotto il nome, disallineati e senza gerarchia.
//
// Il pannello e' in portal su document.body, non dentro la sidebar: la sidebar
// ha `overflow: hidden` (serve al fondale decorativo e all'animazione di
// collasso), quindi un popover posizionato "sopra" il trigger verrebbe
// tagliato se restasse nel suo DOM. Stesso approccio di Select.js per la
// tendina dei filtri: posizione calcolata da getBoundingClientRect(), non CSS
// relativo al genitore.
function UserMenu({ user, collapsed, onOpenSettings, onLogout }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const confirm = useConfirm();

  const initial = ((user && user.username) || '?').charAt(0).toUpperCase();
  const roleLabel = user ? (ROLE_LABEL[user.role] || user.role) : '';

  const close = useCallback(() => setOpen(false), []);

  // Ancorato al BASSO del trigger (che sta in fondo alla sidebar): il pannello
  // si apre verso l'alto, altrimenti finirebbe sotto il bordo della finestra.
  useLayoutEffect(() => {
    if (!open) return;
    const updateRect = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ bottom: window.innerHeight - r.top + 8, left: r.left });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open]);

  // Click fuori dal trigger E fuori dal pannello (che vive in un altro
  // sottoalbero del DOM, quindi va controllato con un ref a parte). Il click
  // sul trigger non chiude nulla qui: lo gestisce il suo stesso onClick.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const handleLogout = async () => {
    close();
    if (!(await confirm({
      title: 'Esci',
      message: 'Uscire dal portale di gestione?',
      confirmLabel: 'Esci',
      tone: 'danger',
      icon: 'logout',
    }))) return;
    onLogout();
  };

  if (!user) return null;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="sidebar-user"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={collapsed ? user.username : undefined}
      >
        <div className="user-avatar">{initial}</div>
        <div className="sidebar-user-meta">
          <strong>{user.username}</strong>
          <span className={`role-badge role-${user.role}`}>{roleLabel}</span>
        </div>
        <Icon name="chevronUp" size={14} className="sidebar-user-caret" />
      </button>

      {open && rect && createPortal(
        <div
          ref={panelRef}
          className="user-menu-panel"
          role="menu"
          aria-label={`Menu account di ${user.username}`}
          style={{ bottom: rect.bottom, left: rect.left }}
        >
          {/* Nome e ruolo sono gia' nel trigger appena sopra: qui solo le azioni. */}
          <div className="user-menu-list">
            <button
              type="button"
              role="menuitem"
              className="user-menu-item"
              onClick={() => { onOpenSettings(); close(); }}
            >
              <Icon name="sliders" size={16} />
              Impostazioni
            </button>
            <button
              type="button"
              role="menuitem"
              className="user-menu-item user-menu-item--danger"
              onClick={handleLogout}
            >
              <Icon name="logout" size={16} />
              Esci
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default UserMenu;
